import { useState, useEffect } from "react";
import { toast } from "sonner";
import { User, Mail, Shield, Key, FileSignature, Loader2, Check } from "lucide-react";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Profile({ user, setUser }) {
  const [loading, setLoading] = useState(false);
  
  // Password change
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Signature
  const [signature, setSignature] = useState(user?.signature_html || "");
  const [savingSignature, setSavingSignature] = useState(false);

  useEffect(() => {
    if (user?.signature_html) {
      setSignature(user.signature_html);
    }
  }, [user]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword) {
      toast.error("Please fill in all password fields");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    setChangingPassword(true);
    try {
      const response = await fetch(`${API}/auth/change-password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      if (response.ok) {
        toast.success("Password changed successfully");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to change password");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveSignature = async () => {
    setSavingSignature(true);
    try {
      const response = await fetch(`${API}/auth/signature`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ signature_html: signature }),
      });

      if (response.ok) {
        toast.success("Signature saved successfully");
        if (setUser) {
          setUser({ ...user, signature_html: signature });
        }
      } else {
        toast.error("Failed to save signature");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setSavingSignature(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">My Profile</h1>
        <p className="text-slate-600">Manage your account settings and preferences</p>
      </div>

      {/* User Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label className="text-slate-500">Name</Label>
              <p className="font-medium text-slate-900">{user.name}</p>
            </div>
            <div>
              <Label className="text-slate-500">Email</Label>
              <p className="font-medium text-slate-900 flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-400" />
                {user.email}
              </p>
            </div>
            <div>
              <Label className="text-slate-500">Role</Label>
              <Badge variant={user.role === "admin" ? "default" : "secondary"} className="mt-1">
                <Shield className="w-3 h-3 mr-1" />
                {user.role === "admin" ? "Administrator" : "User"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="password" className="w-full">
        <TabsList>
          <TabsTrigger value="password">
            <Key className="w-4 h-4 mr-2" />
            Change Password
          </TabsTrigger>
          <TabsTrigger value="signature">
            <FileSignature className="w-4 h-4 mr-2" />
            Email Signature
          </TabsTrigger>
        </TabsList>

        {/* Change Password Tab */}
        <TabsContent value="password">
          <Card>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
              <CardDescription>
                Update your password to keep your account secure
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                <div>
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="mt-1"
                    data-testid="current-password"
                  />
                </div>
                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="mt-1"
                    data-testid="new-password"
                  />
                  <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="mt-1"
                    data-testid="confirm-password"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={changingPassword}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="change-password-btn"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Signature Tab */}
        <TabsContent value="signature">
          <Card>
            <CardHeader>
              <CardTitle>Email Signature</CardTitle>
              <CardDescription>
                Your signature will be automatically added to emails you send
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="signature">Signature (HTML supported)</Label>
                <Textarea
                  id="signature"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder={`<p>Best regards,</p>
<p><strong>${user.name}</strong></p>
<p>RESILIGENT</p>
<p>Email: ${user.email}</p>`}
                  className="mt-1 min-h-[200px] font-mono text-sm"
                  data-testid="signature-textarea"
                />
              </div>
              
              {signature && (
                <div>
                  <Label className="text-slate-500">Preview</Label>
                  <div 
                    className="mt-2 p-4 border rounded-lg bg-slate-50"
                    dangerouslySetInnerHTML={{ __html: signature }}
                  />
                </div>
              )}

              <Button 
                onClick={handleSaveSignature}
                disabled={savingSignature}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-signature-btn"
              >
                {savingSignature ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save Signature
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
